import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ClipboardList, ExternalLink, RefreshCw, Eye, Plus, MapPin, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import ChantierQualificationDetailDialog from './ChantierQualificationDetailDialog';

// Bloc compact Qualification Travaux d’optimisation — inséré juste AU-DESSUS de Géoportail
// (spec §9 §10 §11 §12). Non bloquant : jamais imposé, jamais auto-ouvert.
// Le clic sur « Remplir » ouvre valnexo.fr avec un token signé (15 min).
export default function ChantierQualificationBlock({ prospect, isParrain = false, isVP = false, isAdmin = false }) {
    const { user: currentUser } = useAuth();
    const canEditDocs = !isParrain; // parrain read-only sur docs
    const [summary, setSummary] = useState(null);
    const [sites, setSites] = useState([]);
    const [selectedSite, setSelectedSite] = useState('__none__'); // '__none__' = dossier entier
    const [issuing, setIssuing] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [siteDialogOpen, setSiteDialogOpen] = useState(false);
    const [newSite, setNewSite] = useState({ label: '', address: '', postal_code: '', city: '' });
    const [savingSite, setSavingSite] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null); // { qual_id, label }
    const [deleting, setDeleting] = useState(false);

    // Suppression du formulaire de qualification (admin_full uniquement).
    const isAdminFull = currentUser?.role === 'admin_full' || currentUser?.is_admin === true;
    const handleDeleteQualification = async () => {
        if (!deleteConfirm?.qual_id) return;
        setDeleting(true);
        try {
            await api.delete(`/chantier-qualifications/${deleteConfirm.qual_id}`);
            toast.success('Formulaire de qualification supprimé');
            setDeleteConfirm(null);
            await loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Suppression impossible');
        } finally { setDeleting(false); }
    };

    const loadAll = async () => {
        try {
            const [s, sitesResp] = await Promise.all([
                api.get(`/prospects/${prospect.id}/chantier-energy-summary`),
                api.get(`/prospects/${prospect.id}/sites`),
            ]);
            setSummary(s.data);
            setSites(sitesResp.data || []);
        } catch {
            setSummary({ status: 'none' });
            setSites([]);
        }
    };
    useEffect(() => { loadAll(); }, [prospect.id]);

    const openForm = async () => {
        setIssuing(true);
        try {
            const site_id = selectedSite === '__none__' ? null : selectedSite;
            const r = await api.post('/qualifications/issue-token', {
                prospect_id: prospect.id,
                site_id,
            });
            window.open(r.data.url, '_blank', 'noopener,noreferrer');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Impossible d\'ouvrir le formulaire');
        } finally { setIssuing(false); }
    };

    const saveNewSite = async () => {
        if (!newSite.label.trim()) {
            toast.error('Le nom du site est obligatoire');
            return;
        }
        setSavingSite(true);
        try {
            const r = await api.post(`/prospects/${prospect.id}/sites`, {
                prospect_id: prospect.id,
                label: newSite.label.trim(),
                address: newSite.address.trim() || null,
                postal_code: newSite.postal_code.trim() || null,
                city: newSite.city.trim() || null,
            });
            const created = r.data;
            setSites((prev) => [...prev, created]);
            setSelectedSite(created.id);
            setSiteDialogOpen(false);
            setNewSite({ label: '', address: '', postal_code: '', city: '' });
            toast.success(`Site « ${created.label} » créé`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Impossible de créer le site');
        } finally { setSavingSite(false); }
    };

    const noteColor = (n) => n >= 4 ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
        : n >= 3 ? 'bg-blue-100 text-blue-800 border-blue-300'
        : n >= 2 ? 'bg-amber-100 text-amber-800 border-amber-300'
        : 'bg-slate-100 text-slate-700 border-slate-300';

    // Sélecteur de site : n'apparaît que si le prospect a au moins un site OU si l'utilisateur peut en créer
    const canCreateSite = isAdmin || isVP;
    const showSiteSelector = sites.length > 0 || canCreateSite;

    const siteSelectorJsx = showSiteSelector ? (
        <div className="flex items-center gap-2 flex-wrap" data-testid="chantier-qualif-site-row">
            <MapPin className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs text-slate-600">Site :</span>
            <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger className="h-8 text-xs w-auto min-w-[180px]" data-testid="chantier-qualif-site-select">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="__none__">Dossier entier (pas de site spécifique)</SelectItem>
                    {sites.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                            {s.label}{s.city ? ` — ${s.city}` : ''}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {canCreateSite && (
                <Button size="sm" variant="ghost" onClick={() => setSiteDialogOpen(true)}
                    className="h-8 text-xs px-2" data-testid="chantier-qualif-add-site-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Nouveau site
                </Button>
            )}
        </div>
    ) : null;

    const quickSiteDialogJsx = (
        <Dialog open={siteDialogOpen} onOpenChange={setSiteDialogOpen}>
            <DialogContent className="max-w-md" data-testid="chantier-qualif-site-dialog">
                <DialogHeader>
                    <DialogTitle>Nouveau site pour ce dossier</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label htmlFor="site-label">Nom du site *</Label>
                        <Input id="site-label" value={newSite.label}
                            onChange={(e) => setNewSite({ ...newSite, label: e.target.value })}
                            placeholder="Ex. Cave principale, Entrepôt Nord…"
                            data-testid="chantier-qualif-site-label" />
                    </div>
                    <div>
                        <Label htmlFor="site-address">Adresse (facultatif)</Label>
                        <Input id="site-address" value={newSite.address}
                            onChange={(e) => setNewSite({ ...newSite, address: e.target.value })}
                            data-testid="chantier-qualif-site-address" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <div>
                            <Label htmlFor="site-cp">CP</Label>
                            <Input id="site-cp" value={newSite.postal_code}
                                onChange={(e) => setNewSite({ ...newSite, postal_code: e.target.value })}
                                data-testid="chantier-qualif-site-cp" />
                        </div>
                        <div className="col-span-2">
                            <Label htmlFor="site-city">Ville</Label>
                            <Input id="site-city" value={newSite.city}
                                onChange={(e) => setNewSite({ ...newSite, city: e.target.value })}
                                data-testid="chantier-qualif-site-city" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setSiteDialogOpen(false)}>Annuler</Button>
                    <Button onClick={saveNewSite} disabled={savingSite}
                        data-testid="chantier-qualif-site-save-btn">
                        {savingSite ? 'Création…' : 'Créer le site'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    const deleteQualifDialogJsx = (
        <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
            <DialogContent className="max-w-md" data-testid="chantier-qualif-delete-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-rose-700">
                        <AlertTriangle className="w-5 h-5" /> Supprimer le formulaire de qualification ?
                    </DialogTitle>
                    <DialogDescription>
                        Cette action est <b>irréversible</b> côté utilisateur. La qualification
                        {deleteConfirm?.label ? ` « ${deleteConfirm.label} »` : ''} et ses documents seront supprimés.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                        Annuler
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDeleteQualification}
                        disabled={deleting}
                        data-testid="chantier-qualif-delete-confirm-btn"
                    >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        {deleting ? 'Suppression…' : 'Supprimer définitivement'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    // État 1 : Aucune qualification
    if (!summary || summary.status === 'none') {
        return (
            <>
                <Card className="border-slate-200 bg-slate-50/60" data-testid="chantier-qualif-block-empty">
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-slate-800 text-base">
                            <ClipboardList className="w-4 h-4" /> Formulaire de qualification
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-slate-600 space-y-3">
                        <p>
                            Ce formulaire n&apos;est <b>pas obligatoire</b>. Il est toutefois fortement conseillé
                            pour mieux identifier le potentiel énergétique du site, les équipements à étudier,
                            les travaux envisageables et préparer efficacement le rendez-vous.
                        </p>
                        <p className="text-xs text-slate-500">
                            Plus il est complété, plus la qualification sera fiable. Les documents restent facultatifs.
                        </p>
                        {siteSelectorJsx}
                        <Button onClick={openForm} disabled={issuing}
                            className="bg-slate-900 hover:bg-slate-800"
                            data-testid="chantier-qualif-open-btn">
                            {issuing ? 'Ouverture…' : <>Remplir le formulaire <ExternalLink className="w-3.5 h-3.5 ml-2" /></>}
                        </Button>
                    </CardContent>
                </Card>
                {quickSiteDialogJsx}
            </>
        );
    }

    // État 2 : Brouillon en cours
    if (summary.status === 'draft') {
        const d = summary.draft;
        return (
            <>
                <Card className="border-amber-200 bg-amber-50/40" data-testid="chantier-qualif-block-draft">
                    <CardContent className="py-3 space-y-2">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="text-sm">
                                <b>Formulaire commencé — Brouillon</b>
                                <span className="text-slate-500"> · Complétude {d?.completeness || 0}% · maj {new Date(d?.updated_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                            <div className="flex gap-2">
                                <Button onClick={() => setDetailOpen(true)} size="sm" variant="ghost"
                                    data-testid="chantier-qualif-view-detail-btn">
                                    <Eye className="w-3.5 h-3.5 mr-1.5" /> Voir détail
                                </Button>
                                <Button onClick={openForm} size="sm" variant="outline" data-testid="chantier-qualif-continue-btn">
                                    Continuer <ExternalLink className="w-3.5 h-3.5 ml-2" />
                                </Button>
                                {isAdminFull && (
                                    <Button
                                        onClick={() => setDeleteConfirm({ qual_id: d.id, label: 'Brouillon en cours' })}
                                        size="sm"
                                        variant="ghost"
                                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                        data-testid="chantier-qualif-delete-draft-btn"
                                        title="Supprimer le formulaire de qualification"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                            </div>
                        </div>
                        {siteSelectorJsx}
                    </CardContent>
                </Card>
                <ChantierQualificationDetailDialog open={detailOpen} onOpenChange={setDetailOpen} qualification={d} currentUser={currentUser} canEdit={canEditDocs} />
                {quickSiteDialogJsx}
                {deleteQualifDialogJsx}
            </>
        );
    }

    // État 3 : Validé — synthèse compacte
    const q = summary.qualification;
    return (
        <>
            <Card className="border-slate-200" data-testid="chantier-qualif-block-validated">
                <CardContent className="py-3 space-y-2">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <Badge className={`border ${noteColor(q.display_note)}`}>{q.display_note}/4</Badge>
                                {q.is_provisional && <Badge variant="outline" className="text-[10px]">Provisoire</Badge>}
                                <span className="text-xs text-slate-500">
                                    Complétude {q.completeness}% · maj {new Date(q.validated_at || q.updated_at).toLocaleDateString('fr-FR')}
                                </span>
                            </div>
                            <p className="text-sm text-slate-700 line-clamp-2">{q.appreciation}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                            <Button onClick={() => setDetailOpen(true)} size="sm" variant="ghost"
                                data-testid="chantier-qualif-view-detail-btn">
                                <Eye className="w-3.5 h-3.5 mr-1.5" /> Voir détail
                            </Button>
                            {(isAdmin || isVP) && (
                                <Button onClick={openForm} size="sm" variant="outline" data-testid="chantier-qualif-edit-btn">
                                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Modifier
                                </Button>
                            )}
                            {isAdminFull && (
                                <Button
                                    onClick={() => setDeleteConfirm({ qual_id: q.id, label: `Note ${q.display_note}/4` })}
                                    size="sm"
                                    variant="ghost"
                                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                    data-testid="chantier-qualif-delete-validated-btn"
                                    title="Supprimer le formulaire de qualification"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            )}
                        </div>
                    </div>
                    {siteSelectorJsx}
                    <p className="text-[11px] text-slate-400 italic leading-relaxed" data-testid="chantier-qualif-disclaimer">
                        Estimation issue d&apos;une pré-étude. La rentabilité définitive du dossier est confirmée par l&apos;étude technique du bureau d&apos;études.
                    </p>
                </CardContent>
            </Card>
            <ChantierQualificationDetailDialog open={detailOpen} onOpenChange={setDetailOpen} qualification={q} currentUser={currentUser} canEdit={canEditDocs} />
            {quickSiteDialogJsx}
            {deleteQualifDialogJsx}
        </>
    );
}
